import React, { useCallback } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  TtFlavorContext,
  TtMenuLabel,
  TtMenu,
  TtMenuItem,
  TtIcon,
} from '@23/ui-react'

import { useAllFeedsGrouped } from '../../../firebase/hooks/useFloor'
import {
  setEditingFeed,
  updateEditingMix,
  useEditingMixFeed,
  useEditingMixState,
} from '../../../firebase/hooks/useMixes'
import type { Feed, FeedMedia, Mix } from '../../../firebase/model/mix'
import { copy } from '../../../global/utils/valtio'

const MixSlotAssignMenu = () => {
  const feeds = useAllFeedsGrouped()
  const mix = useEditingMixState()
  const { idx: feedIdx } = useEditingMixFeed()
  const feed = feedIdx !== null ? mix?.feeds[feedIdx] : undefined

  const handleMediaChange = useCallback(
    (newFeed: FeedMedia | undefined) => {
      if (feedIdx === null) return
      const newMix = copy(mix) as Mix
      if (newFeed) {
        newMix.feeds.forEach((f, fIdx, array) => {
          if (
            f.media &&
            f.media.type === newFeed.type &&
            (f.media.type === 'tray' || f.media.id === newFeed.id)
          ) {
            // eslint-disable-next-line no-param-reassign
            delete array[fIdx].media
          }
        })
      }
      newMix.feeds[feedIdx].media = newFeed

      setEditingFeed(feedIdx, 'initial')
      updateEditingMix(newMix)
    },
    [feedIdx, mix]
  )

  if (!feed) return null

  const isMediaSlot = feed.type === 'media' || feed.type === 'all'
  const isSpeakerSlot = feed.type === 'camera' || feed.type === 'all'

  return (
    <TtFlavorContext flavor="webinars">
      <TtMenu
        ref={(el) => {
          if (el?.popover) el.showPopover()
        }}
        className="mix-slot-assign-menu"
        // @ts-expect-error — this is a valid attribute
        popover="manual"
        style={{ positionAnchor: `--slot-${feedIdx}` }}
        onTtSelect={(event) => {
          const newFeedMedia = event.detail.item.value.split('|')

          handleMediaChange({
            type: newFeedMedia[0] as NonNullable<Feed['media']>['type'],
            id: +newFeedMedia[1],
          })
        }}
      >
        {isMediaSlot && feeds.tray.length > 0 && (
          <>
            {feeds.tray.map((f) => (
              <TtMenuItem
                key={f.id}
                value={`tray|${f.id}`}
                checked={feed.media?.type === 'tray'}
              >
                <TtIcon
                  slot="prefix"
                  library="one/webinars"
                  name="tray-slides"
                />
                WebinarTray
              </TtMenuItem>
            ))}
          </>
        )}
        {isSpeakerSlot && feeds.camera.length > 0 && (
          <>
            <TtMenuLabel>
              <FormattedMessage defaultMessage="Speakers" />
            </TtMenuLabel>
            {feeds.camera.map((f) => (
              <TtMenuItem
                key={f.id}
                value={`camera|${f.id}`}
                checked={
                  feed.media?.type === 'camera' && feed.media.id === f.id
                }
              >
                <TtIcon slot="prefix" library="one/webinars" name="camera-on" />
                {f.name}
              </TtMenuItem>
            ))}
          </>
        )}
        {isMediaSlot && feeds.screen.length > 0 && (
          <>
            <TtMenuLabel>
              <FormattedMessage defaultMessage="Screen Sharing" />
            </TtMenuLabel>
            {feeds.screen.map((f) => (
              <TtMenuItem
                key={f.id}
                value={`screen|${f.id}`}
                checked={
                  feed.media?.type === 'screen' && feed.media.id === f.id
                }
              >
                <TtIcon
                  slot="prefix"
                  library="one/webinars"
                  name="screen-share"
                />
                {f.name}
              </TtMenuItem>
            ))}
          </>
        )}
        {isSpeakerSlot && feeds.mobile.length > 0 && (
          <>
            <TtMenuLabel>
              <FormattedMessage defaultMessage="2ndCam" />
            </TtMenuLabel>
            {feeds.mobile.map((f) => (
              <TtMenuItem
                key={f.id}
                value={`mobile|${f.id}`}
                checked={
                  feed.media?.type === 'mobile' && feed.media.id === f.id
                }
              >
                {f.id}
              </TtMenuItem>
            ))}
          </>
        )}
      </TtMenu>
    </TtFlavorContext>
  )
}

export { MixSlotAssignMenu }
